import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type OpsData = {
  reconciliationPending?: { count: number; total: number };
  movementsMissingCategory?: { count: number; total: number };
  movementsMissingCostCenter?: { count: number; total: number };
  overduePayables?: { count: number; total: number };
  overdueReceivables?: { count: number; total: number };
  dataQuality?: {
    payablesMissingAccount: number;
    receivablesMissingAccount: number;
    movementsMissingAccount: number;
    movementsMissingOrigin: number;
  };
};

type MovementDetail = {
  id: number;
  description: string;
  amount: number;
  occurredAt: string;
};

type TitleDetail = {
  id: number;
  description: string;
  amount: number;
  dueDate: string;
};

export const OpsDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>(() => getCurrentMonthRange());
  const [data, setData] = useState<OpsData | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/dash/ops?${query}`);
      if (!response.ok) {
        console.error("Ops dashboard failed", { status: response.status });
        setData(null);
        setError("Falha ao carregar dashboard.");
        setLoading(false);
        return;
      }
      setData((await response.json()) as OpsData);
      setLoading(false);
    };
    fetchData();
  }, [client, query]);

  const reconQuery = usePagedQuery<MovementDetail>({
    endpoint: "/dash/ops/pendencias-conciliacao",
    filters,
  });

  const missingQuery = usePagedQuery<MovementDetail>({
    endpoint: "/dash/ops/movimentos-sem-classificacao",
    filters,
  });

  const overdueQuery = usePagedQuery<TitleDetail>({
    endpoint: "/dash/ops/titulos-vencidos",
    filters: { ...filters, kind: "payable" },
  });

  const cards = [
    {
      key: "recon",
      title: "Pendencias de conciliacao",
      value: data?.reconciliationPending?.count ?? 0,
      subtitle: `Valor ${formatMoneyBR(data?.reconciliationPending?.total ?? 0)}`,
      detail: "recon",
    },
    {
      key: "missing",
      title: "Movimentos sem classificacao",
      value: (data?.movementsMissingCategory?.count ?? 0) +
        (data?.movementsMissingCostCenter?.count ?? 0),
      subtitle: "Sem categoria ou centro de custo",
      detail: "missing",
    },
    {
      key: "overdue",
      title: "Titulos vencidos",
      value:
        (data?.overduePayables?.count ?? 0) +
        (data?.overdueReceivables?.count ?? 0),
      subtitle: "Pagar e receber",
      detail: "overdue",
    },
  ];

  const qualityData = [
    {
      label: "AP sem conta",
      total: data?.dataQuality?.payablesMissingAccount ?? 0,
    },
    {
      label: "AR sem conta",
      total: data?.dataQuality?.receivablesMissingAccount ?? 0,
    },
    {
      label: "Mov sem conta",
      total: data?.dataQuality?.movementsMissingAccount ?? 0,
    },
    {
      label: "Mov manual",
      total: data?.dataQuality?.movementsMissingOrigin ?? 0,
    },
  ];

  return (
    <div className="container py-4">
      <h1 className="mb-3">Dashboard Operacional</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />
      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row g-3">
        {cards.map((card) => (
          <div className="col-md-4" key={card.key}>
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted">{card.title}</h6>
                <h3>{card.value}</h3>
                <p className="text-muted">{card.subtitle}</p>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setDetail(card.detail)}
                >
                  Ver detalhes
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Qualidade de dados</h5>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={qualityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="total" fill="#0d6efd" name="Ocorrencias" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Resumo financeiro</h5>
              <p className="text-muted mb-1">
                Pendencias conciliacao: {data?.reconciliationPending?.count ?? 0}
              </p>
              <p className="text-muted mb-1">
                Titulos vencidos:{" "}
                {(data?.overduePayables?.count ?? 0) +
                  (data?.overdueReceivables?.count ?? 0)}
              </p>
              <p className="text-muted mb-0">
                Movimentos sem categoria: {data?.movementsMissingCategory?.count ?? 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {detail === "recon" && (
          <PageState loading={reconQuery.loading} error={reconQuery.error}>
            <DataTable<MovementDetail>
              title="Pendencias de conciliacao"
              rows={reconQuery.rows}
              total={reconQuery.total}
              page={reconQuery.page}
              take={reconQuery.take}
              onPageChange={reconQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Data", render: (item) => formatDateBR(item.occurredAt) },
              ]}
            />
          </PageState>
        )}
        {detail === "missing" && (
          <PageState loading={missingQuery.loading} error={missingQuery.error}>
            <DataTable<MovementDetail>
              title="Movimentos sem categoria/centro"
              rows={missingQuery.rows}
              total={missingQuery.total}
              page={missingQuery.page}
              take={missingQuery.take}
              onPageChange={missingQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Data", render: (item) => formatDateBR(item.occurredAt) },
              ]}
            />
          </PageState>
        )}
        {detail === "overdue" && (
          <PageState loading={overdueQuery.loading} error={overdueQuery.error}>
            <DataTable<TitleDetail>
              title="Titulos vencidos (pagar)"
              rows={overdueQuery.rows}
              total={overdueQuery.total}
              page={overdueQuery.page}
              take={overdueQuery.take}
              onPageChange={overdueQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Vencimento", render: (item) => formatDateBR(item.dueDate) },
              ]}
            />
          </PageState>
        )}
      </div>
    </div>
  );
};
